/**
 * vmx-cart-drawer.js
 * Defines the <vmx-cart-drawer> Web Component.
 *
 * Security: NEVER uses innerHTML for any user-controlled content.
 * All text via textContent; markup via cloneNode(true) from <template> elements.
 *
 * Events consumed:
 *   vmx:cart:opened  — open drawer + fetch cart
 *   vmx:cart:updated — re-fetch + re-render
 *
 * Events dispatched:
 *   vmx:cart:closed  — fired when drawer closes
 *
 * Globals read:
 *   window.VMX_FREE_DELIVERY_THRESHOLD  (pence; brand default 3000 = £30)
 *   window.VMX_FREE_GIFT_THRESHOLD      (pence; brand default 3999 = £39.99)
 *   window.VMX_GIFT_VARIANT_ID          (string variant id, may be blank)
 *   window.Shopify.formatMoney          (optional, Shopify helper)
 */

(function () {
  'use strict';

  var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

  /* ── Money formatter ────────────────────────────────────────────── */
  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, '£{{amount}}');
    }
    return (cents / 100).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' });
  }

  /* ── Cart Ajax API helpers ──────────────────────────────────────── */
  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function cartChange(payload) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) { return r.json(); });
  }

  function cartAdd(payload) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload),
    }).then(function (r) { return r.json(); });
  }

  /* ── Gift threshold logic — tiered: 1 gift per full threshold ──── */
  function maybeManageGift(cart) {
    var threshold = window.VMX_FREE_GIFT_THRESHOLD;
    var giftId = (window.VMX_GIFT_VARIANT_ID || '').trim();

    if (!threshold) return Promise.resolve(cart);
    if (!giftId) {
      console.warn('[vmx-cart] VMX_GIFT_VARIANT_ID is blank — skipping gift add. Populate in theme settings after Phase 6.');
      return Promise.resolve(cart);
    }

    var giftLineIndex = -1;
    var currentGiftQty = 0;
    var nonGiftSubtotal = 0;

    cart.items.forEach(function (item, idx) {
      var isGift = item.properties && item.properties._is_gift === 'true';
      if (isGift) {
        giftLineIndex = idx + 1; // Shopify lines are 1-indexed
        currentGiftQty = item.quantity;
      } else {
        nonGiftSubtotal += item.final_line_price;
      }
    });

    // One free gift per full threshold tier (buy £40 → 1 free, £80 → 2 free…).
    var desiredGiftQty = Math.max(0, Math.floor(nonGiftSubtotal / threshold));
    if (desiredGiftQty === currentGiftQty) return Promise.resolve(cart);

    if (giftLineIndex === -1) {
      // No gift line yet → add desired quantity in one POST.
      return cartAdd({
        items: [{ id: giftId, quantity: desiredGiftQty, properties: { _is_gift: 'true' } }],
      }).then(fetchCart);
    }

    // Gift line exists → bump or drop to the desired qty (0 removes it).
    return cartChange({ line: giftLineIndex, quantity: desiredGiftQty }).then(fetchCart);
  }

  /* ── Progress bar helpers ───────────────────────────────────────── */
  function buildProgressMessage(nonGiftSubtotal) {
    var deliveryThreshold = window.VMX_FREE_DELIVERY_THRESHOLD || 3000;
    var giftThreshold = window.VMX_FREE_GIFT_THRESHOLD;
    var deliveryHit = nonGiftSubtotal >= deliveryThreshold;

    // The theme only "owns" the free gift when a gift variant is configured in
    // settings. Otherwise a discount app (e.g. SMART) runs the gift and shows its
    // own progress — so we must NOT print a gift threshold the app might not match,
    // and we never name a specific gift product (the gift is flexible/app-driven).
    var giftActive = !!(window.VMX_GIFT_VARIANT_ID || '').trim() && giftThreshold;

    if (giftActive) {
      var giftHit = nonGiftSubtotal >= giftThreshold;
      var giftQty = Math.floor(nonGiftSubtotal / giftThreshold);
      if (giftHit) {
        var nextTier = (giftQty + 1) * giftThreshold;
        return 'Unlocked: free delivery + free gift. Add ' + formatMoney(nextTier - nonGiftSubtotal) + ' for another.';
      }
      if (deliveryHit) {
        return 'Add ' + formatMoney(giftThreshold - nonGiftSubtotal) + ' more for a free gift.';
      }
      return 'Add ' + formatMoney(deliveryThreshold - nonGiftSubtotal) + ' more for free UK delivery.';
    }

    // Gift handled by an app (or off) → show only the Shopify-native delivery bar.
    if (deliveryHit) return 'You’ve unlocked free UK delivery.';
    return 'Add ' + formatMoney(deliveryThreshold - nonGiftSubtotal) + ' more for free UK delivery.';
  }

  /* ── Web Component ──────────────────────────────────────────────── */
  class VmxCartDrawer extends HTMLElement {
    constructor() {
      super();
      this._open = false;
      this._nodes = null;
      this._previousFocus = null;
      this._handleKeyDown = this._handleKeyDown.bind(this);
      this._handleCartOpen = this._handleCartOpen.bind(this);
      this._handleCartUpdated = this._handleCartUpdated.bind(this);
    }

    connectedCallback() {
      document.addEventListener('vmx:cart:opened', this._handleCartOpen);
      document.addEventListener('vmx:cart:updated', this._handleCartUpdated);
    }

    disconnectedCallback() {
      document.removeEventListener('vmx:cart:opened', this._handleCartOpen);
      document.removeEventListener('vmx:cart:updated', this._handleCartUpdated);
      this._unbindKeyDown();
    }

    /* ── Event handlers ───────────────────────────────────────────── */
    _handleCartOpen() {
      if (this._open) return;
      this._mount();
      fetchCart()
        .then(function (cart) { return maybeManageGift(cart); })
        .then(this.render.bind(this))
        .then(this._openDrawer.bind(this))
        .catch(function (err) {
          // Never leave the page in a half-open state: log and bail cleanly so
          // the body stays scrollable and a retry (re-click) can work.
          console.error('[vmx-cart] Failed to open cart:', err);
        });
    }

    _handleCartUpdated() {
      if (!this._nodes) return;
      fetchCart()
        .then(function (cart) { return maybeManageGift(cart); })
        .then(this.render.bind(this))
        .catch(function (err) { console.error('[vmx-cart] Failed to refresh cart:', err); });
    }

    /* ── Mount: clone drawer template ────────────────────────────── */
    _mount() {
      if (this._nodes) return;

      var tmpl = document.getElementById('vmx-cart-drawer-tmpl');
      if (!tmpl) {
        console.error('[vmx-cart] Template #vmx-cart-drawer-tmpl not found.');
        return;
      }

      var fragment = tmpl.content.cloneNode(true);
      this.appendChild(fragment);

      this._nodes = {
        backdrop:        this.querySelector('[data-vmx-cart-backdrop]'),
        panel:           this.querySelector('.vmx-cart-drawer'),
        closeButtons:    this.querySelectorAll('[data-vmx-cart-close]'),
        count:           this.querySelector('[data-vmx-cart-count]'),
        progressWrap:    this.querySelector('[data-vmx-cart-progress]'),
        progressFill:    this.querySelector('[data-vmx-cart-progress-fill]'),
        progressMsg:     this.querySelector('[data-vmx-cart-progress-msg]'),
        milestoneDelivery: this.querySelector('[data-vmx-cart-milestone="delivery"]'),
        milestoneGift:   this.querySelector('[data-vmx-cart-milestone="gift"]'),
        linesSlot:       this.querySelector('[data-vmx-cart-lines]'),
        footer:          this.querySelector('[data-vmx-cart-footer]'),
        savings:         this.querySelector('[data-vmx-cart-savings]'),
        savingsAmount:   this.querySelector('[data-vmx-cart-savings-amount]'),
        subtotalLabel:   this.querySelector('[data-vmx-cart-subtotal-label]'),
        subtotal:        this.querySelector('[data-vmx-cart-subtotal]'),
        subtotalWas:     this.querySelector('[data-vmx-cart-subtotal-was]'),
        shippingNote:    this.querySelector('[data-vmx-cart-shipping-note]'),
        checkoutLink:    this.querySelector('[data-vmx-cart-checkout]'),
        checkoutLabel:   this.querySelector('[data-vmx-cart-checkout-label]'),
      };

      this._bindClose();
    }

    /* ── Render cart data ─────────────────────────────────────────── */
    render(cart) {
      if (!this._nodes) return;
      var n = this._nodes;

      var totalQty = cart.item_count || 0;
      var items = cart.items || [];

      var nonGiftSubtotal = 0;
      items.forEach(function (item) {
        var isGift = item.properties && item.properties._is_gift === 'true';
        if (!isGift) nonGiftSubtotal += item.final_line_price;
      });

      // Badge count
      if (n.count) {
        n.count.textContent = totalQty;
        n.count.hidden = totalQty === 0;
      }

      // Progress bar
      this._renderProgress(nonGiftSubtotal);

      // Lines
      n.linesSlot.replaceChildren();
      if (items.length === 0) {
        var emptyTmpl = document.getElementById('vmx-cart-empty-tmpl');
        if (emptyTmpl) {
          n.linesSlot.appendChild(emptyTmpl.content.cloneNode(true));
        }
        if (n.footer) n.footer.hidden = true;
        return;
      }

      items.forEach(function (item, idx) {
        n.linesSlot.appendChild(this._buildLineNode(item, idx + 1));
      }, this);

      // Footer
      this._renderFooter(items, nonGiftSubtotal, totalQty, cart.total_price);
    }

    /* ── Render progress bar ──────────────────────────────────────── */
    _renderProgress(nonGiftSubtotal) {
      var n = this._nodes;
      var deliveryThreshold = window.VMX_FREE_DELIVERY_THRESHOLD || 3000;
      var giftThreshold = window.VMX_FREE_GIFT_THRESHOLD || 3999;
      var cap = giftThreshold;
      var fillPct = Math.min((nonGiftSubtotal / cap) * 100, 100);
      var deliveryPct = (deliveryThreshold / cap) * 100;
      var deliveryHit = nonGiftSubtotal >= deliveryThreshold;
      var giftHit = nonGiftSubtotal >= giftThreshold;

      if (n.progressFill) n.progressFill.style.width = fillPct + '%';
      if (n.progressMsg) n.progressMsg.textContent = buildProgressMessage(nonGiftSubtotal);
      if (n.progressWrap) n.progressWrap.classList.toggle('is-unlocked', deliveryHit);

      // Position delivery milestone
      if (n.milestoneDelivery) {
        n.milestoneDelivery.style.left = deliveryPct + '%';
        n.milestoneDelivery.classList.toggle('is-hit', deliveryHit);
      }
      if (n.milestoneGift) {
        n.milestoneGift.classList.toggle('is-hit', giftHit);
      }
    }

    /* ── Build one line node ──────────────────────────────────────── */
    _buildLineNode(item, lineIndex) {
      var tmpl = document.getElementById('vmx-cart-line-tmpl');
      if (!tmpl) return document.createTextNode('');

      var node = tmpl.content.cloneNode(true);
      var isGift = item.properties && item.properties._is_gift === 'true';

      // Image
      var img = node.querySelector('.vmx-cart-line__image');
      if (img) {
        if (item.image) {
          img.setAttribute('src', item.image);
          img.setAttribute('alt', item.title || '');
        } else {
          img.hidden = true;
        }
      }

      // Gift badge
      var giftBadge = node.querySelector('.vmx-cart-line__gift-badge');
      if (giftBadge) giftBadge.hidden = !isGift;

      // Title
      var title = node.querySelector('[data-vmx-line-title]');
      if (title) title.textContent = item.product_title || item.title || '';

      // Meta
      var meta = node.querySelector('[data-vmx-line-meta]');
      if (meta) {
        if (isGift) {
          meta.textContent = 'Free gift · auto-applied';
        } else {
          var variantTitle = (item.variant_title && item.variant_title !== 'Default Title') ? item.variant_title : '';
          var purchaseType = item.selling_plan_allocation ? 'Subscribe & Save' : 'One-time';
          meta.textContent = [variantTitle, purchaseType].filter(Boolean).join(' · ');
        }
      }

      // Remove button
      var removeBtn = node.querySelector('[data-vmx-line-remove]');
      if (removeBtn) {
        if (isGift) {
          removeBtn.hidden = true;
        } else {
          removeBtn.addEventListener('click', function () {
            if (removeBtn.disabled) return;
            removeBtn.disabled = true;
            cartChange({ line: lineIndex, quantity: 0 })
              .then(function () {
                document.dispatchEvent(new CustomEvent('vmx:cart:updated'));
              })
              .catch(function (err) {
                console.error('[vmx-cart] Remove failed:', err);
                removeBtn.disabled = false;
              });
          });
        }
      }

      // Stepper
      var stepper = node.querySelector('[data-vmx-line-stepper]');
      var qtySpan = node.querySelector('[data-vmx-line-qty]');
      if (isGift) {
        if (stepper) stepper.hidden = true;
      } else {
        if (qtySpan) qtySpan.textContent = item.quantity;

        var decrementBtn = node.querySelector('[data-vmx-line-decrement]');
        var incrementBtn = node.querySelector('[data-vmx-line-increment]');
        // Disable BOTH steppers during an in-flight change: the captured
        // item.quantity / lineIndex are stale until the next re-render, so a
        // rapid second click would post a wrong qty or hit a shifted line.
        var lockStepper = function () {
          if (decrementBtn) decrementBtn.disabled = true;
          if (incrementBtn) incrementBtn.disabled = true;
        };
        var unlockStepper = function () {
          if (decrementBtn) decrementBtn.disabled = false;
          if (incrementBtn) incrementBtn.disabled = false;
        };
        if (decrementBtn) {
          decrementBtn.addEventListener('click', function () {
            if (decrementBtn.disabled) return;
            lockStepper();
            var newQty = Math.max(0, item.quantity - 1);
            cartChange({ line: lineIndex, quantity: newQty })
              .then(function () {
                document.dispatchEvent(new CustomEvent('vmx:cart:updated'));
              })
              .catch(function (err) { console.error('[vmx-cart] Update failed:', err); unlockStepper(); });
          });
        }
        if (incrementBtn) {
          incrementBtn.addEventListener('click', function () {
            if (incrementBtn.disabled) return;
            lockStepper();
            cartChange({ line: lineIndex, quantity: item.quantity + 1 })
              .then(function () {
                document.dispatchEvent(new CustomEvent('vmx:cart:updated'));
              })
              .catch(function (err) { console.error('[vmx-cart] Update failed:', err); unlockStepper(); });
          });
        }
      }

      // A line is "free" when its final price is 0 — whether a property gift or a
      // 100%-off free unit (e.g. Buy 2 Get 1). Show its real worth either way.
      var isFree = item.final_line_price === 0;

      // Gift / free value text — show the real retail worth so the customer sees the win.
      var giftValue = node.querySelector('[data-vmx-line-gift-value]');
      if (giftValue) {
        if (isFree && item.original_line_price > 0) {
          giftValue.textContent = 'Yours free — worth ' + formatMoney(item.original_line_price);
          giftValue.hidden = false;
        } else if (isGift) {
          giftValue.textContent = 'Yours free';
          giftValue.hidden = false;
        } else {
          giftValue.hidden = true;
        }
      }

      // Price
      var priceEl = node.querySelector('[data-vmx-line-price]');
      if (priceEl) {
        if (isFree) {
          priceEl.textContent = 'FREE';
          priceEl.classList.add('vmx-cart-line__price--free');
        } else {
          priceEl.textContent = formatMoney(item.final_line_price);
          priceEl.classList.remove('vmx-cart-line__price--free');
        }
      }

      // Compare-at (struck RRP) — show whenever the line is discounted below its RRP.
      var compareEl = node.querySelector('[data-vmx-line-compare]');
      if (compareEl) {
        if (item.original_line_price > item.final_line_price) {
          compareEl.textContent = formatMoney(item.original_line_price);
          compareEl.hidden = false;
        } else {
          compareEl.hidden = true;
        }
      }

      return node;
    }

    /* ── Render footer ────────────────────────────────────────────── */
    _renderFooter(items, nonGiftSubtotal, totalQty, totalPrice) {
      var n = this._nodes;
      if (!n.footer) return;
      n.footer.hidden = false;

      // Subtotal
      if (n.subtotalLabel) {
        n.subtotalLabel.textContent = 'Subtotal (' + totalQty + ' item' + (totalQty !== 1 ? 's' : '') + ')';
      }
      if (n.subtotal) n.subtotal.textContent = formatMoney(nonGiftSubtotal);

      // Savings (compare original vs final for non-gift items)
      var originalTotal = items.reduce(function (sum, item) {
        var isGift = item.properties && item.properties._is_gift === 'true';
        return isGift ? sum : sum + item.original_line_price;
      }, 0);
      var savings = Math.max(0, originalTotal - nonGiftSubtotal);

      if (n.savings) {
        n.savings.hidden = savings === 0;
        if (savings > 0 && n.savingsAmount) {
          n.savingsAmount.textContent = formatMoney(savings);
        }
      }
      if (n.subtotalWas) {
        if (savings > 0) {
          n.subtotalWas.textContent = formatMoney(originalTotal);
          n.subtotalWas.hidden = false;
        } else {
          n.subtotalWas.hidden = true;
        }
      }

      // Shipping note
      var deliveryThreshold = window.VMX_FREE_DELIVERY_THRESHOLD || 3000;
      if (n.shippingNote) {
        n.shippingNote.textContent = 'Shipping calculated at checkout · Free over ' + formatMoney(deliveryThreshold);
      }

      // Checkout label
      if (n.checkoutLabel) {
        n.checkoutLabel.textContent = 'Checkout · ' + formatMoney(nonGiftSubtotal);
      }
    }

    /* ── Bind close events ────────────────────────────────────────── */
    _bindClose() {
      var self = this;
      if (this._nodes.backdrop) {
        this._nodes.backdrop.addEventListener('click', function () { self.close(); });
      }
      this._nodes.closeButtons.forEach(function (btn) {
        btn.addEventListener('click', function () { self.close(); });
      });
    }

    /* ── Open / close ─────────────────────────────────────────────── */
    _openDrawer() {
      this._open = true;
      this._previousFocus = document.activeElement;
      document.body.style.overflow = 'hidden';
      this._bindKeyDown();
      requestAnimationFrame(function () {
        var first = this.querySelector(FOCUSABLE);
        if (first) first.focus();
      }.bind(this));
    }

    open() {
      document.dispatchEvent(new CustomEvent('vmx:cart:opened'));
    }

    close() {
      if (!this._open) return;
      this._open = false;
      document.body.style.overflow = '';
      this._unbindKeyDown();

      var panel = this._nodes && this._nodes.panel;
      var backdrop = this._nodes && this._nodes.backdrop;
      var self = this;

      function cleanup() {
        self._nodes = null;
        while (self.firstChild) self.removeChild(self.firstChild);
        document.dispatchEvent(new CustomEvent('vmx:cart:closed'));
      }

      if (panel) {
        panel.classList.add('is-closing');
        if (backdrop) backdrop.style.opacity = '0';
        setTimeout(cleanup, 320);
      } else {
        cleanup();
      }

      if (this._previousFocus) {
        this._previousFocus.focus();
        this._previousFocus = null;
      }
    }

    /* ── Focus trap / keyboard ────────────────────────────────────── */
    _bindKeyDown() { document.addEventListener('keydown', this._handleKeyDown); }
    _unbindKeyDown() { document.removeEventListener('keydown', this._handleKeyDown); }

    _handleKeyDown(e) {
      if (e.key === 'Escape') { this.close(); return; }
      if (e.key === 'Tab') {
        var focusable = Array.from(this.querySelectorAll(FOCUSABLE));
        if (!focusable.length) return;
        var first = focusable[0];
        var last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) { e.preventDefault(); last.focus(); }
        } else {
          if (document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }
    }
  }

  customElements.define('vmx-cart-drawer', VmxCartDrawer);

  /* ── Header badge click → open cart ────────────────────────────── */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-vmx-open-cart]');
    if (btn) {
      e.preventDefault();
      document.dispatchEvent(new CustomEvent('vmx:cart:opened'));
    }
  });
})();
